// Reglas de prospectos que comparten el drawer del wizard y la bandeja del portal: el catálogo de
// motivos, qué cuenta como abierto y cómo se arma el body del POST /prospects.
import { mxDay, mxDayOffset, mxDayStartUtc } from '@/lib/dateHelpers';
import type {
    CreateProspectRequest,
    PendingReason,
    Prospect,
    ProspectByPhone,
    ProspectStatus,
    WizardStepId,
    WizardStepSlug,
} from './types';

/**
 * Los 18 motivos de `PendingReasonEnum`, con la etiqueta que ve el vendedor.
 * 🔴 Ninguna etiqueta puede nombrar la causa del bloqueo: avisarla es delito (ver `blocked.ts`).
 */
export const PENDING_REASON_LABEL: Record<PendingReason, string> = {
    OTP_FAILED: 'No llegó el código',
    NO_INE: 'No trae identificación',
    NO_OCCUPATION: 'No define ocupación',
    DISLIKES_CATALOG: 'No le gustó el catálogo',
    PRICE_HIGH: 'Precio alto',
    EVALUATING_CONDITIONS: 'Evaluando condiciones',
    REJECTED_RETRY: 'Rechazado, va a reintentar',
    INSTALLMENT_HIGH: 'Mensualidad alta',
    EVALUATING_DOWNPAYMENT: 'Evaluando el enganche',
    EVALUATING_TERM: 'Evaluando el plazo',
    NO_MONEY_TODAY: 'No trae el dinero hoy',
    CONTRACT_DOUBTS: 'Dudas con el contrato',
    DEVICE_ISSUE: 'Problema con el equipo',
    CLIENT_THINKING: 'Lo está pensando',
    OTHER: 'Otro',
    // Los pone el silo al trabarse la venta. Los tres dicen el ESTADO de la apertura y ninguno su
    // causa: engloban varias a propósito, porque nombrar la que aplica sería aviso indebido.
    ACCOUNT_UNDER_REVIEW: 'En revisión adicional',
    // La apertura se frenó y la destraba un tercero: dice el estado, nunca quién ni por qué.
    ACCOUNT_OPEN_FAILED: 'Apertura detenida',
    ACCOUNT_REJECTED: 'Cuenta no autorizada',
};

/**
 * Motivos que escribe el silo cuando la venta se traba, nunca el vendedor. Se traducen en la tabla,
 * el filtro y la ficha, pero se filtran del desplegable de «Guardar y salir».
 */
export const SYSTEM_PENDING_REASONS: readonly PendingReason[] = [
    'ACCOUNT_UNDER_REVIEW',
    'ACCOUNT_OPEN_FAILED',
    'ACCOUNT_REJECTED',
];

/** Pasos donde el wizard ofrece «Guardar y salir» (convención de `pantallas-wizard.html`). */
export const DRAWER_STEPS: readonly WizardStepId[] = [
    '01',
    '02',
    '03',
    '04',
    '05',
    '06',
    '07',
    '08',
    '09',
];

/**
 * Motivos que ofrece cada paso. Todos salen del catálogo del silo: uno inventado da 400.
 * El reparto es el del mock (`prospectos-drawer.js` §MOTIVOS_POR_PASO): ofrecerle «cuota alta» a
 * quien todavía no vio una cotización es ruido.
 */
const REASONS_BY_STEP: Partial<Record<WizardStepId, readonly PendingReason[]>> = {
    // El 01 es el del OTP: si el código no llegó, el vendedor guarda y agenda desde ahí.
    '01': ['OTP_FAILED', 'CLIENT_THINKING', 'OTHER'],
    '02': ['NO_INE', 'NO_OCCUPATION', 'CLIENT_THINKING', 'OTHER'],
    '03': ['DISLIKES_CATALOG', 'PRICE_HIGH', 'CLIENT_THINKING', 'OTHER'],
    // El 04 es el router de forma de pago: la cotización todavía no se mostró, así que los motivos
    // de plazo/enganche/mensualidad no le sirven al vendedor acá.
    '04': ['EVALUATING_CONDITIONS', 'CLIENT_THINKING', 'OTHER'],
    '05': ['REJECTED_RETRY', 'EVALUATING_CONDITIONS', 'CLIENT_THINKING', 'OTHER'],
    '06': [
        'INSTALLMENT_HIGH',
        'EVALUATING_DOWNPAYMENT',
        'EVALUATING_TERM',
        'CLIENT_THINKING',
        'OTHER',
    ],
    '07': ['NO_MONEY_TODAY', 'EVALUATING_DOWNPAYMENT', 'CLIENT_THINKING', 'OTHER'],
    '08': ['CONTRACT_DOUBTS', 'CLIENT_THINKING', 'OTHER'],
    '09': ['DEVICE_ISSUE', 'CLIENT_THINKING', 'OTHER'],
};

const FALLBACK_REASONS: readonly PendingReason[] = ['CLIENT_THINKING', 'OTHER'];

export function isPendingReason(value: string): value is PendingReason {
    return Object.hasOwn(PENDING_REASON_LABEL, value);
}

/**
 * Lo que el vendedor puede elegir en el drawer: el catálogo del paso, menos los de sistema, más
 * `current` — el motivo ya guardado, que sin estar entre las opciones el `Select` pinta en blanco.
 */
export function reasonOptionsForStep(
    step: WizardStepId,
    current?: PendingReason,
): { value: PendingReason; label: string }[] {
    const reasons = REASONS_BY_STEP[step] ?? FALLBACK_REASONS;
    const offered = reasons.filter((value) => !SYSTEM_PENDING_REASONS.includes(value));
    const add = current && isPendingReason(current) && !offered.includes(current) ? current : null;
    // Antes de «Otro», que en el mock siempre cierra la lista.
    const values = add
        ? [...offered.filter((v) => v !== 'OTHER'), add, ...offered.filter((v) => v === 'OTHER')]
        : offered;
    return values.map((value) => ({ value, label: PENDING_REASON_LABEL[value] }));
}

/**
 * Estados que dejan al prospecto fuera de la agenda del vendedor: los dos que ya terminaron, y el
 * que espera a un tercero — perseguir con «vencido hace 3 días» una venta que no depende de él le
 * ensucia la bandeja sin darle nada que hacer.
 */
const SETTLED_STATUSES: readonly ProspectStatus[] = ['CLOSED', 'CANCELLED', 'LOST', 'UNDER_REVIEW'];

export function isProspectOpen(prospect: Prospect): boolean {
    return !SETTLED_STATUSES.includes(prospect.status);
}

export function prospectName(prospect: Prospect): string {
    return prospect.newCustomer?.name ?? 'Cliente registrado';
}

export function prospectPhone(prospect: Prospect): string | undefined {
    return prospect.newCustomer?.phone;
}

export function prospectReasonText(prospect: Prospect): string {
    // Un motivo que el silo agregue después se muestra crudo, no en blanco.
    const label = isPendingReason(prospect.pendingReason)
        ? PENDING_REASON_LABEL[prospect.pendingReason]
        : prospect.pendingReason;
    return prospect.freeReason ? `${label} · ${prospect.freeReason}` : label;
}

/**
 * Motivo con el que se marca perdido desde la bandeja. `markLost` PISA `freeReason`, así que el
 * texto libre original se reenvía para no perderlo.
 */
export function lostReasonFor(prospect: Prospect): string {
    return prospect.freeReason || prospectReasonText(prospect);
}

export type ProspectBucket = 'overdue' | 'today' | 'upcoming';

/** El día de calendario de hoy en el piso de venta (`YYYY-MM-DD`), el corte de la agenda. */
export function todayMx(): string {
    return mxDayOffset(0);
}

/**
 * En qué tramo de la agenda cae el prospecto, comparando días de calendario de CDMX. Contra la
 * medianoche del navegador, un vendedor en Tijuana vería vencido todo lo agendado para hoy.
 */
export function prospectBucket(prospect: Prospect, today: string): ProspectBucket {
    const day = mxDay(prospect.nextContactAt);
    if (day < today) return 'overdue';
    if (day === today) return 'today';
    return 'upcoming';
}

export interface ProspectFormValues {
    name: string;
    phone: string;
    pendingReason: PendingReason;
    freeReason: string;
    /** Día del calendario (`YYYY-MM-DD`) que teclea el vendedor en el input de fecha. */
    nextContactDay: string;
    notes: string;
    productSku?: string;
    /** La venta de la que sale el prospecto: el único camino de vuelta del prospecto a su sesión. */
    wizardSessionId?: string;
    /** Pantalla desde la que se guardó: a ella vuelve «Reabrir wizard». */
    resumeSlug?: WizardStepSlug;
    /** Sobreescribe el prospecto que ese teléfono ya tenía: hace viajar los campos vacíos, porque
     * el patch del silo solo toca lo que llega y sin ellos borrarlos no los borra. */
    overwritesExisting?: boolean;
}

/** Lo que el drawer precarga de un campo del formulario. `''` es "el vendedor todavía no eligió". */
export interface ProspectPrefill {
    name: string;
    pendingReason: PendingReason | '';
    freeReason: string;
    /** Ausente cuando el prospecto no trae agenda: se queda el default del drawer, que es mañana. */
    nextContactDay?: string;
    notes: string;
    /** La agenda guardada ya pasó y se subió a hoy: el vendedor tiene que enterarse. */
    scheduleWasOverdue: boolean;
}

/** Lo guardado del prospecto con la forma del formulario del drawer. Sin teléfono: ese lo manda
 * la sesión viva. */
export function prospectFormPrefill(saved: ProspectByPhone, today: string): ProspectPrefill {
    const day = saved.nextContactAt ? mxDay(saved.nextContactAt) : undefined;
    const overdue = day !== undefined && day < today;
    return {
        scheduleWasOverdue: overdue,
        name: saved.customerName ?? '',
        // El motivo de sistema se conserva: reenviarlo es lo único que preserva la marca de la
        // venta trabada.
        pendingReason: saved.pendingReason ?? '',
        freeReason: saved.freeReason ?? '',
        // El input tiene `min` en hoy: una agenda vencida no se puede volver a elegir con el
        // calendario y el navegador la marca inválida.
        nextContactDay: overdue ? today : day,
        notes: saved.notes ?? '',
    };
}

/**
 * Arma el body de `POST /prospects`. Solo declara campos del DTO del silo: cualquier otro lo
 * descarta `excludeExtraneousValues` sin avisar.
 */
export function buildCreateProspectRequest(values: ProspectFormValues): CreateProspectRequest {
    const freeReason = values.pendingReason === 'OTHER' ? values.freeReason.trim() : '';
    const notes = values.notes.trim();
    const overwrites = values.overwritesExisting === true;
    return {
        newCustomer: { name: values.name.trim(), phone: values.phone },
        pendingReason: values.pendingReason,
        ...(freeReason || overwrites ? { freeReason } : {}),
        ...(values.productSku ? { productSku: values.productSku } : {}),
        ...(values.wizardSessionId ? { wizardSessionId: values.wizardSessionId } : {}),
        // Sin sesión el silo lo ignora: no se manda.
        ...(values.wizardSessionId && values.resumeSlug ? { resumeSlug: values.resumeSlug } : {}),
        ...(values.nextContactDay ? { nextContactAt: mxDayStartUtc(values.nextContactDay) } : {}),
        ...(notes || overwrites ? { notes } : {}),
    };
}
